import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowDown, CalendarDays } from "lucide-react";
import { Reveal, Stagger, StaggerItem, MaskLines } from "@/components/motion";
import Marquee from "@/components/Marquee";
import SectionHeading from "@/components/SectionHeading";
import { IMAGES, OVERVIEW, CLUB, NEWS } from "@/data/site";

const STATS = [
  { value: "1948", label: "Anno di fondazione" },
  { value: "76+", label: "Anni di storia sportiva" },
  { value: "2014", label: "Stella d'Oro al Merito CONI" },
  { value: "200+", label: "Atleti tesserati" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative flex min-h-[100svh] items-center overflow-hidden bg-cab-deep"
      >
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
          <img
            src={IMAGES.heroWrestling}
            alt="Atleti di lotta"
            className="h-full w-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </motion.div>
        <div className="absolute inset-0 bg-hero-scrim" />
        <div className="absolute inset-0 grain" />

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative mx-auto w-full max-w-[1400px] px-5 pt-28 pb-24 md:px-8"
        >
          <Reveal>
            <div className="mb-7 flex items-center gap-3">
              <span className="h-px w-12 bg-cream/60" />
              <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-cream/85">
                Bologna · Lotta & Pesistica · dal {CLUB.founded}
              </span>
            </div>
          </Reveal>

          <h1 className="font-display text-[15vw] font-extrabold uppercase leading-[0.88] tracking-tighter text-cream sm:text-8xl lg:text-[8.5rem]">
            <MaskLines lines={["Club Atletico", "Bologna"]} delay={0.15} stagger={0.13} />
          </h1>

          <Reveal delay={0.6}>
            <div className="mt-4 flex items-center gap-4">
              <span className="font-display text-2xl font-extrabold tracking-[0.4em] text-cab-light">— CAB —</span>
            </div>
          </Reveal>

          <Reveal delay={0.72}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-cream/85 md:text-lg">
              Dal 1948 alleniamo forza, tecnica e carattere nel cuore di Bologna. Due
              discipline olimpiche, una sola grande famiglia sportiva. Benvenuto al CAB.
            </p>
          </Reveal>

          <Reveal delay={0.85}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/chi-siamo"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-cream px-7 py-4 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5"
              >
                Scopri il club
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/dove-siamo"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-cream/40 px-7 py-4 font-bold text-cream backdrop-blur-sm transition-colors duration-300 hover:bg-cream/10"
              >
                Prova gratuita
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-cream/70 md:flex"
        >
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.3em]">Scorri</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown className="h-4 w-4" />
          </motion.span>
        </motion.div>
      </section>

      {/* ============ MARQUEE ============ */}
      <div className="border-y border-ink/10 bg-cream py-6">
        <Marquee
          items={["Lotta", "Pesistica", "1948", "Bologna", "Comunità", "Tradizione"]}
          outline
        />
      </div>

      {/* ============ INTRO / MANIFESTO ============ */}
      <section className="bg-cream py-20 md:py-32" data-testid="home-intro">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cab">
                  Benvenuto al CAB
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="mt-6 font-display text-3xl font-bold leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-[2.9rem]">
                  Non è solo una palestra. È il posto dove <span className="text-cab">forza</span> e{" "}
                  <span className="text-cab">rispetto</span> si allenano insieme, generazione dopo generazione.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:pt-4">
              <Reveal delay={0.1}>
                <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
                  Nato nel 1948, il Club Atletico Bologna raccoglie l'eredità degli atleti
                  che resero Bologna capitale della lotta greco-romana. Insignito della Stella
                  d'Oro al Merito Sportivo CONI, oggi è una palestra nel cuore di Bologna — in
                  via Fioravanti, presso Piazza Lucio Dalla — aperta a tutti, dai giovani alla terza età.
                </p>
                <Link
                  to="/chi-siamo"
                  data-testid="intro-link-chi-siamo"
                  className="group mt-7 inline-flex items-center gap-2 font-bold text-cab"
                >
                  La nostra storia
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>

          {/* Stats */}
          <Stagger className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 md:mt-24 md:grid-cols-4">
            {STATS.map((s) => (
              <StaggerItem key={s.label} className="bg-cream">
                <div className="p-8 md:p-10">
                  <div className="font-display text-5xl font-extrabold tracking-tight text-cab md:text-6xl">
                    {s.value}
                  </div>
                  <div className="mt-2 text-sm font-medium text-muted-foreground">{s.label}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============ OVERVIEW GRID ============ */}
      <section className="bg-white py-20 md:py-32" data-testid="home-overview">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <SectionHeading
              overline="Esplora il club"
              title="Sei sezioni, una sola passione"
              subtitle="Tutto quello che devi sapere sul Club Atletico Bologna, dalla nostra storia agli allenamenti quotidiani."
            />
          </div>

          <Stagger className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 md:mt-20">
            {OVERVIEW.map((item) => (
              <StaggerItem key={item.to}>
                <Link
                  to={item.to}
                  data-testid={`overview-card-${item.to.slice(1)}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-ink/10 bg-cream transition-all duration-500 hover:-translate-y-1.5 hover:border-cab/40 hover:shadow-[0_20px_50px_rgba(62,106,158,0.15)]"
                >
                  <div className="relative aspect-[16/11] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cab-deep/70 via-transparent to-transparent" />
                    <span className="absolute left-5 top-5 font-display text-sm font-bold text-cream/90">
                      {item.n}
                    </span>
                    <span className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream/90 text-cab opacity-0 transition-all duration-500 group-hover:opacity-100">
                      <ArrowUpRight className="h-5 w-5" />
                    </span>
                    <h3 className="absolute bottom-4 left-5 font-display text-2xl font-extrabold uppercase tracking-tight text-cream">
                      {item.title}
                    </h3>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="flex-1 text-[15px] leading-relaxed text-muted-foreground">
                      {item.text}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 font-bold text-cab">
                      Scopri di più
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============ NEWS ============ */}
      <section className="bg-cream py-20 md:py-32" data-testid="home-news">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading overline="News · Evento" title="Ultime dal club" />
          <div className="mt-14 grid gap-10 md:mt-20 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl">
                <img src={NEWS.image} alt={NEWS.title} loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
                <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-cream/90 px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.15em] text-cab">
                  <CalendarDays className="h-4 w-4" />
                  {NEWS.date}
                </span>
              </div>
            </Reveal>
            <div>
              <Reveal>
                <h3 className="font-display text-3xl font-extrabold leading-[1.05] tracking-tight text-ink md:text-4xl">
                  {NEWS.title}
                </h3>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-muted-foreground md:text-base">
                  {NEWS.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CLOSING CTA ============ */}
      <section className="relative overflow-hidden bg-cab-deep py-24 md:py-32" data-testid="home-cta">
        <div className="absolute inset-0 opacity-20">
          <img src={IMAGES.gym} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-[1400px] px-5 text-center md:px-8">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-cream sm:text-6xl">
              Pronto a salire sul tappetino?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-xl text-base text-cream/75 md:text-lg">
              La prima lezione è gratuita. Vieni a conoscerci, porta la tua energia: al resto pensiamo noi.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/dove-siamo"
              data-testid="home-cta-button"
              className="group mt-9 inline-flex items-center gap-2 rounded-md bg-cream px-8 py-4 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5"
            >
              Prenota la tua prova gratuita
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
